import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Hash, CalendarClock, Clock, Flame, BookOpen, Video, Lock, LogIn, KeyRound, PlusCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function Stat({ icon: Icon, label, value, tint = "brand-blue-bg", testid }) {
  return (
    <div className="card-elevated p-5 flex items-center gap-4" data-testid={testid}>
      <div className={`w-12 h-12 rounded-xl ${tint} text-white flex items-center justify-center shrink-0`}>
        <Icon size={22} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#8494B8] font-bold">{label}</div>
        <div className="font-display font-extrabold text-xl text-[#0B3D91] mt-0.5">{value}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) { setLoading(false); return; }
      try {
        const { data: res } = await axios.get(`${API}/my/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setData(res);
      } catch (e) {
        console.error("Dashboard load error:", e);
      } finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  if (!token) {
    return (
      <div className="py-14 sm:py-20">
        <div className="max-w-md mx-auto px-4 sm:px-6">
          <div className="card-elevated p-8 text-center fade-up">
            <Lock size={40} className="mx-auto text-[#F97316]" />
            <h1 className="mt-3 font-display font-extrabold text-2xl text-[#0B3D91]">Il tuo spazio personale</h1>
            <p className="mt-2 text-sm text-[#5A6A94]">Accedi con la tua matricola per vedere questa pagina.</p>
            <Link to="/login" className="btn-primary mt-5 inline-flex"><LogIn size={16} /> Accedi</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="page-bg min-h-[60vh] flex items-center justify-center">
      <div className="card-elevated p-8 text-[#5A6A94]">Caricamento...</div>
    </div>
  );

  if (!data) return (
    <div className="page-bg min-h-[60vh] flex items-center justify-center">
      <div className="card-elevated p-8 text-[#F97316]">Errore caricamento dashboard.</div>
    </div>
  );

  const next = data.next_slot;
  const endDate = data.subscription_end ? new Date(data.subscription_end) : null;

  return (
    <div className="py-10 sm:py-14">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-6">
        {/* Welcome header with matricola */}
        <div className="card-elevated p-6 sm:p-8 fade-up relative overflow-hidden" data-testid="dashboard-header">
          <div className="absolute -top-14 -right-14 w-56 h-56 rounded-full brand-orange-bg opacity-10" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[#F97316]">Benvenuto/a</div>
              <h1 className="mt-1 font-display font-extrabold text-3xl sm:text-4xl text-[#0B3D91]" data-testid="dashboard-greeting">
                Ciao, {data.nome}!
              </h1>
              <p className="mt-1 text-sm text-[#5A6A94]">Questa è la tua area personale. Da qui vedi le prossime lezioni e il tuo progresso.</p>
            </div>
            <div className="rounded-2xl border-2 border-[#DDE4F5] bg-[#F6F9FF] px-5 py-3 text-center" data-testid="dashboard-matricola-box">
              <div className="text-xs font-bold uppercase tracking-widest text-[#8494B8] flex items-center gap-1 justify-center">
                <Hash size={12} /> Matricola
              </div>
              <div className="mt-1 font-mono font-extrabold text-3xl text-[#0B3D91] tracking-widest">{data.matricola}</div>
            </div>
          </div>
        </div>

        {/* Next class banner */}
        {next ? (
          <div className="card-elevated p-6 bg-gradient-to-br from-[#EEF4FF] to-white border-2 border-[#0B3D91]/20 fade-up" data-testid="dashboard-next-class">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-14 h-14 rounded-2xl brand-blue-bg text-white flex items-center justify-center shrink-0">
                <CalendarClock size={26} />
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-widest text-[#F97316]">Prossima lezione</div>
                <div className="font-display font-extrabold text-2xl text-[#0B3D91] mt-0.5">
                  {next.time}
                </div>
                <div className="text-sm text-[#5A6A94]">
                  {next.next_date ? new Date(next.next_date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }) : "lun–ven"}
                  {next.label && <> · <span className="font-semibold text-[#0F1E3D]">{next.label}</span></>}
                </div>
              </div>
              {data.subscription_active && next.meet_link ? (
                <a href={next.meet_link} target="_blank" rel="noopener noreferrer"
                  className="btn-primary" data-testid="dashboard-join-btn">
                  <Video size={16} /> Entra
                </a>
              ) : !data.subscription_active ? (
                <span className="text-sm text-[#F97316] font-semibold inline-flex items-center gap-2">
                  <Lock size={14} /> Rinnova per vedere il link
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="card-elevated p-6 text-sm text-[#8494B8] text-center" data-testid="dashboard-no-class">
            Nessun orario pubblicato. Chiedi all'amministrazione quando partono le lezioni.
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 fade-up">
          <Stat
            icon={Flame}
            label="Streak giorni"
            value={`${data.attendance_streak} ${data.attendance_streak === 1 ? "giorno" : "giorni"}`}
            tint="brand-orange-bg"
            testid="stat-streak"
          />
          <Stat
            icon={CalendarClock}
            label="Abbonamento"
            value={data.subscription_active ? `${data.days_left} giorni` : "Scaduto"}
            tint={data.subscription_active ? "brand-blue-bg" : "bg-[#DC2626]"}
            testid="stat-subscription"
          />
          <Stat
            icon={BookOpen}
            label="Materiali"
            value={`${data.materials_count} disponibili`}
            tint="bg-[#7C3AED]"
            testid="stat-materials"
          />
        </div>

        {/* Attendance strip (last 7 days) */}
        <div className="card-elevated p-5 fade-up" data-testid="dashboard-attendance-strip">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold text-[#0B3D91] text-base">Ultimi 30 giorni</h3>
            <span className="text-xs text-[#8494B8]">{data.attendance_last_30_days} giorni di accesso</span>
          </div>
          <AttendanceStrip count={data.attendance_last_30_days} />
          <p className="text-xs text-[#8494B8] mt-3">Ogni volta che apri la pagina orari o la dashboard, conta come giorno di presenza.</p>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 fade-up" data-testid="dashboard-actions">
          <Link to="/slots" className="btn-outline justify-center" data-testid="action-slots">
            <Clock size={14} /> Orari
          </Link>
          <Link to="/materials" className="btn-outline justify-center" data-testid="action-materials">
            <BookOpen size={14} /> Materiali
          </Link>
          <Link to="/exercises" className="btn-outline justify-center" data-testid="action-exercises">
            📚 Esercizi
          </Link>
          <Link to="/change-password" className="btn-outline justify-center" data-testid="action-change-pw">
            <KeyRound size={14} /> Password
          </Link>
        </div>

        {/* Extra slot request CTA */}
        <ExtraSlotRequestPanel token={token} currentSlotId={data.next_slot?.id} />
      </div>
    </div>
  );
}


function ExtraSlotRequestPanel({ token, currentSlotId }) {
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open || !token) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/slots/public`),
      axios.get(`${API}/my/slot-requests`, { headers: { Authorization: `Bearer ${token}` } }),
    ])
      .then(([a, b]) => { setSlots(a.data || []); setMyRequests(b.data || []); })
      .catch((e) => { console.error("Extra slot panel load failed:", e); toast.error("Errore caricamento"); })
      .finally(() => setLoading(false));
  }, [open, token]);

  const togglePick = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));

  const submit = async () => {
    const ids = Object.keys(picked).filter((k) => picked[k]);
    if (ids.length === 0) { toast.error("Seleziona almeno uno slot"); return; }
    try {
      await axios.post(
        `${API}/my/slot-requests`,
        { slot_ids: ids, message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Richiesta inviata! L'admin la valuterà a breve.");
      setPicked({});
      setMessage("");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richiesta");
    }
  };

  // Pending / recent requests pills
  const pendingCount = myRequests.filter((r) => r.status === "pending").length;

  return (
    <div className="card-elevated p-5 fade-up" data-testid="dashboard-extra-slot">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-[#0B3D91] text-base flex items-center gap-2">
            <PlusCircle size={16} className="text-[#F97316]" /> Vuoi altri orari?
          </h3>
          <p className="text-sm text-[#5A6A94] mt-1">
            Puoi chiedere all'admin di aggiungere uno o più slot extra alla tua area personale. {pendingCount > 0 && (
              <span className="font-semibold text-[#F97316]">· {pendingCount} richiesta/e in attesa</span>
            )}
          </p>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="btn-primary !py-2 !px-4 text-sm shrink-0" data-testid="toggle-extra-slot">
          {open ? "Chiudi" : "Richiedi slot extra"}
        </button>
      </div>

      {open && (
        <div className="mt-5 border-t border-[#E4EAF7] pt-4" data-testid="extra-slot-form">
          {loading ? (
            <p className="text-sm text-[#8494B8]">Caricamento...</p>
          ) : (
            <>
              <div className="space-y-2">
                {slots.filter((s) => s.id !== currentSlotId).length === 0 ? (
                  <p className="text-sm text-[#8494B8] italic">Nessun altro slot disponibile al momento.</p>
                ) : (
                  slots.filter((s) => s.id !== currentSlotId).map((s) => {
                    const existing = myRequests.find((r) => r.slot_id === s.id && r.status === "pending");
                    const approved = myRequests.find((r) => r.slot_id === s.id && r.status === "approved");
                    return (
                      <label key={s.id} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        picked[s.id] ? "border-[#F97316] bg-[#FFF7ED]" : "border-[#DDE4F5] hover:border-[#A9B7DC]"
                      } ${(existing || approved) ? "opacity-70 pointer-events-none" : ""}`}
                      data-testid={`extra-slot-${s.id}`}>
                        <input type="checkbox" checked={!!picked[s.id]} onChange={() => togglePick(s.id)}
                          className="w-4 h-4 accent-[#F97316]" disabled={!!(existing || approved)} />
                        <div className="flex-1">
                          <div className="font-display font-bold text-[#0B3D91]">{s.time}</div>
                          {s.label && <div className="text-xs text-[#5A6A94]">{s.label}</div>}
                        </div>
                        {existing && <span className="text-xs text-[#F97316] font-semibold">In attesa</span>}
                        {approved && <span className="text-xs text-[#16A34A] font-semibold">Approvato</span>}
                      </label>
                    );
                  })
                )}
              </div>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="warm-input mt-3 text-sm min-h-[70px]"
                placeholder="Messaggio opzionale per l'admin (es. motivazione, giorni preferiti)"
                maxLength={500}
                data-testid="extra-slot-message"
              />
              <div className="mt-3 flex gap-2 justify-end">
                <button onClick={() => setOpen(false)} className="btn-ghost text-sm">Annulla</button>
                <button onClick={submit} className="btn-primary !py-2 !px-4 text-sm" data-testid="extra-slot-submit">
                  <PlusCircle size={14} /> Invia richiesta
                </button>
              </div>
              {myRequests.length > 0 && (
                <div className="mt-5 pt-4 border-t border-[#E4EAF7]">
                  <div className="text-xs font-bold uppercase tracking-widest text-[#8494B8] mb-2">Tue richieste recenti</div>
                  <ul className="space-y-1 text-sm">
                    {myRequests.slice(0, 5).map((r) => (
                      <li key={r.id} className="flex items-center gap-2 text-[#5A6A94]">
                        <span className="font-mono text-xs">{new Date(r.created_at).toLocaleDateString("it-IT")}</span>
                        <span>→ slot richiesto</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          r.status === "pending" ? "bg-[#FFF7E6] text-[#B45309]" :
                          r.status === "approved" ? "bg-[#E8F8EE] text-[#16A34A]" :
                          "bg-[#FFF4EC] text-[#F97316]"
                        }`}>{r.status === "pending" ? "In attesa" : r.status === "approved" ? "Approvata" : "Rifiutata"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AttendanceStrip({ count }) {
  // Visual: 30 dots, highlight count of them (we don't know exactly which days — this is a progress bar vibe)
  const total = 30;
  const highlighted = Math.min(count, total);
  return (
    <div className="flex flex-wrap gap-1">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={`dot-${i}`}
          className={`w-5 h-5 rounded ${
            i < highlighted ? "brand-orange-bg" : "bg-[#EEF4FF]"
          }`}
          title={i < highlighted ? "Attivo" : "Nessun accesso"}
        />
      ))}
    </div>
  );
}

export { DAYS_IT };
